import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { GET_PLATFORM_SUPPORTED_LOCALES, GET_STORE_RECEIPT_SETTINGS } from "src/constants/queries";
import { UPDATE_RESTAURANT } from "src/constants/mutations";
import { SECOND_LANGUAGE_OPTIONS } from "src/constants/constants";
import { successToast } from "src/utils/toasts";
import { suggestedPosLocalesForReceiptSecondLanguage } from "src/utils/localeBridge";
import { BtnLoader } from "src/Loader";
import CustomSelect from "src/components/common/CustomSelect";
import FormRowVertical from "src/components/common/FormRowVertical";

type PlatformLocale = {
  code: string;
  label: string;
  nativeLabel: string;
};

const DEFAULT_POS_LOCALE = "en-US";
const MAX_POS_LANGUAGES = 2;

const normalizeEnabledLocales = (locales: string[], defaultLocale: string) => {
  const uniqueLocales = Array.from(new Set(locales));
  const localesWithDefault = uniqueLocales.includes(defaultLocale)
    ? uniqueLocales
    : [defaultLocale, ...uniqueLocales];

  return [
    defaultLocale,
    ...localesWithDefault.filter((locale) => locale !== defaultLocale),
  ].slice(0, MAX_POS_LANGUAGES);
};

const LanguageSettings = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [platformLocales, setPlatformLocales] = useState<PlatformLocale[]>([]);
  const [secondLanguage, setSecondLanguage] = useState<string>("");
  const [defaultLocale, setDefaultLocale] = useState<string>(DEFAULT_POS_LOCALE);
  const [enabledLocales, setEnabledLocales] = useState<string[]>([DEFAULT_POS_LOCALE]);
  const [initialSecondLanguage, setInitialSecondLanguage] = useState<string | null>(null);
  const [initialDefaultLocale, setInitialDefaultLocale] = useState<string>(DEFAULT_POS_LOCALE);
  const [initialEnabledLocales, setInitialEnabledLocales] = useState<string[]>([DEFAULT_POS_LOCALE]);

  const fetchPlatformLocales = useCallback(async () => {
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_PLATFORM_SUPPORTED_LOCALES,
      });
      const locales = data?.data?.platformSupportedLocales || [];
      setPlatformLocales(locales);
    } catch (e) {
      console.error("Error fetching platform locales:", e);
    }
  }, [axBe]);

  const fetchSettings = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_STORE_RECEIPT_SETTINGS,
        variables: { retrieveStoresInput: { storeCorrelationIds: [hasRestaurant.correlationId] } },
      });
      const stores = data?.data?.stores || [];
      const s = stores[0] || null;
      const receiptSecond = s?.secondLanguage ?? "";
      const storeDefault = s?.defaultLocale || DEFAULT_POS_LOCALE;
      const rawStoreEnabled = Array.isArray(s?.enabledLocales) && s.enabledLocales.length > 0
        ? s.enabledLocales
        : [DEFAULT_POS_LOCALE];
      const storeEnabled = normalizeEnabledLocales(rawStoreEnabled, storeDefault);

      setSecondLanguage(receiptSecond || "");
      setInitialSecondLanguage(receiptSecond ?? null);
      setDefaultLocale(storeDefault);
      setEnabledLocales(storeEnabled);
      setInitialDefaultLocale(storeDefault);
      setInitialEnabledLocales(storeEnabled);
    } catch (e) {
      console.error("Error fetching language settings:", e);
    }
  }, [axBe, hasRestaurant?.correlationId]);

  useEffect(() => {
    fetchPlatformLocales();
    fetchSettings();
  }, [fetchPlatformLocales, fetchSettings]);

  const enabledLocaleOptions = useMemo(
    () => platformLocales.filter((locale) => enabledLocales.includes(locale.code)),
    [platformLocales, enabledLocales]
  );

  const hasChanged = () =>
    (initialSecondLanguage ?? "") !== (secondLanguage ?? "") ||
    initialDefaultLocale !== defaultLocale ||
    JSON.stringify([...initialEnabledLocales].sort()) !== JSON.stringify([...enabledLocales].sort());

  const toggleEnabledLocale = (code: string, checked: boolean) => {
    setEnabledLocales((prev) => {
      const next = checked
        ? normalizeEnabledLocales([...prev, code], defaultLocale)
        : prev.filter((entry) => entry !== code);
      if (next.length === 0) {
        return [DEFAULT_POS_LOCALE];
      }
      if (!next.includes(defaultLocale)) {
        setDefaultLocale(next[0]);
      }
      return next;
    });
  };

  const onReceiptSecondLanguageChange = (value: string) => {
    setSecondLanguage(value ?? "");
    const suggested = suggestedPosLocalesForReceiptSecondLanguage(value);
    if (suggested.length === 0) {
      return;
    }
    setEnabledLocales((prev) => {
      const next = normalizeEnabledLocales([...prev, ...suggested], defaultLocale);
      if (!next.includes(defaultLocale)) {
        setDefaultLocale(next[0]);
      }
      return next;
    });
  };

  const onSave = async () => {
    if (!hasRestaurant?.correlationId) return;
    const normalizedDefaultCandidate = enabledLocales.includes(defaultLocale)
      ? defaultLocale
      : enabledLocales[0] || DEFAULT_POS_LOCALE;
    const normalizedEnabled = normalizeEnabledLocales(
      enabledLocales.length > 0 ? enabledLocales : [DEFAULT_POS_LOCALE],
      normalizedDefaultCandidate
    );
    const normalizedDefault = normalizedEnabled.includes(normalizedDefaultCandidate)
      ? normalizedDefaultCandidate
      : normalizedEnabled[0];

    setLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_RESTAURANT,
        variables: {
          updateStoreInput: {
            storeCorrelationId: hasRestaurant.correlationId,
            secondLanguage: secondLanguage || "",
            defaultLocale: normalizedDefault,
            enabledLocales: normalizedEnabled,
          },
        },
      });
      if (data?.errors?.length) throw data.errors;
      setInitialSecondLanguage(secondLanguage || null);
      setInitialDefaultLocale(normalizedDefault);
      setInitialEnabledLocales(normalizedEnabled);
      setDefaultLocale(normalizedDefault);
      setEnabledLocales(normalizedEnabled);
      await fetchSettings();
      successToast("Language settings updated!");
    } catch (error) {
      console.error("Error updating language settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const receiptOptions = SECOND_LANGUAGE_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
  const defaultLocaleOptions = enabledLocaleOptions.map((locale) => ({
    value: locale.code,
    label: `${locale.nativeLabel} (${locale.label})`,
  }));

  return (
    <div className="w-full max-w-[760px] space-y-4">
      <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]">
        <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-1">POS languages</h2>
        <p className="text-[14px] text-[#6b7280] mb-4">
          Choose which languages staff can switch to on the desktop POS. The default language is always enabled.
        </p>

        <FormRowVertical label="Default language" className="mt-2">
          <div className="mt-1">
            <CustomSelect
              options={defaultLocaleOptions}
              value={defaultLocale}
              onChange={(v) => {
                const nextDefault = v ?? DEFAULT_POS_LOCALE;
                setDefaultLocale(nextDefault);
                setEnabledLocales((prev) => normalizeEnabledLocales(prev, nextDefault));
              }}
              placeholder="Select default language"
              label="Default language"
            />
          </div>
        </FormRowVertical>

        <div className="mt-5 space-y-3">
          <div>
            <p className="text-sm font-medium text-[#111827]">Available languages</p>
            <p className="mt-1 text-xs text-[#6b7280]">
              Enable up to {MAX_POS_LANGUAGES} POS languages.
            </p>
          </div>
          {platformLocales.map((locale) => {
            const checked = enabledLocales.includes(locale.code);
            const isDefault = locale.code === defaultLocale;
            const disableUncheck = isDefault;
            const disableCheck = !checked && enabledLocales.length >= MAX_POS_LANGUAGES;
            return (
              <label
                key={locale.code}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                  checked
                    ? "border-[#FD7657]/30 bg-[#fef3ef]"
                    : disableCheck
                      ? "border-[#E5E7EB] bg-[#F9FAFB] opacity-70"
                      : "border-[#E5E7EB] bg-white"
                }`}
              >
                <input
                  type="checkbox"
                  className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] shrink-0 rounded border border-[#9B9B9B] cursor-pointer appearance-none"
                  checked={checked}
                  disabled={disableUncheck || disableCheck}
                  onChange={(event) => toggleEnabledLocale(locale.code, event.target.checked)}
                />
                <span className="flex-1 text-sm text-[#111827]">
                  {locale.nativeLabel}
                  <span className="text-[#6b7280]"> · {locale.label}</span>
                </span>
                {isDefault ? (
                  <span className="text-xs font-medium text-[#FD7657]">Default</span>
                ) : checked ? (
                  <span className="text-xs text-[#6b7280]">Enabled</span>
                ) : (
                  <span className="text-xs text-[#9B9B9B]">Disabled</span>
                )}
              </label>
            );
          })}
        </div>
      </div>

      <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]">
        <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-4">Receipt language</h2>
        <FormRowVertical label="Second language for receipts" className="mt-2">
          <div className="mt-1">
            <CustomSelect
              options={receiptOptions}
              value={secondLanguage}
              onChange={(v) => onReceiptSecondLanguageChange(v ?? "")}
              placeholder="None"
              label="Second language"
            />
          </div>
          <p className="text-[14px] text-[#6b7280] mt-2">
            Used when printing receipts: if an item has an alternate name, it will be shown using this language. The desktop app uses this for character encoding (e.g. GBK for Simplified Chinese). Selecting Spanish or Vietnamese also enables the matching POS language for menu editing.
          </p>
        </FormRowVertical>
      </div>

      <div className="flex justify-end">
        {loading ? (
          <BtnLoader />
        ) : (
          <button
            type="button"
            disabled={!hasChanged() || loading}
            className={`px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 ${(!hasChanged() || loading) ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={onSave}
          >
            Update
          </button>
        )}
      </div>
    </div>
  );
};

export default LanguageSettings;
